import { CSSProperties, ComponentType } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { ButtonProps } from '@mui/material';

export enum ProgramWizardOperatingMode {
  STANDALONE = 0,
  GRID = 10,
  GENSETS = 20,
  SHUTDOWN = 30,
  LOADSHARE = 40,
  GEN_SET_CYCLE = 50,
  STANDBY = 70,
}

export const ProgramWizardStep = {
  SELECT_PROGRAM: 'select program',
  PEAKSHAVE_MODE: 'peakshave mode',
  INPUT_SETTINGS: 'input settings',
  CONFIRM: 'confirm',
  STARTING: 'starting',
} as const;

export type ProgramWizardStepValue = (typeof ProgramWizardStep)[keyof typeof ProgramWizardStep];

export enum ProgramWizardLoadshareMasterType {
  WEAK_GRID = 'WEAK_GRID',
  STRONG_GRID = 'STRONG_GRID',
  NONE = 'NONE',
}

export type ProgramWizardData = {
  batteryId: string;
  operatingMode: ProgramWizardOperatingMode | null;
  maxInputCurrent: number | null;
  genSetData: {
    1: number | null;
    2: number | null;
  };
  loadShareMasterType: ProgramWizardLoadshareMasterType | null;
  shutDown24vEnabled: boolean | null;
};

export const ProgramWizardCurrentProgramId = {
  STANDBY: 'STANDBY',
  STANDALONE: 'STANDALONE',
  GEN_SET_CYCLE: 'GEN_SET_CYCLE',
  GEN_SET_NIGHT_MODE: 'GEN_SET_NIGHT_MODE',
  GEN_SET_PEAKSHAVE: 'GEN_SET_PEAKSHAVE',
  GRID_PEAKSHAVE: 'GRID_PEAKSHAVE',
  SHUTDOWN: 'SHUTDOWN',
} as const;

export type ProgramWizardCurrentProgram = {
  id: string;
  description?: string;
};

export type ProgramWizardBatteryConfig = {
  target_soc_lower_vs_gen_set?: number;
  target_soc_hysteresis_vs_gen_set?: number;
  nominal_battery_power?: number;
  soc_setpoint?: number;
};

export type ProgramWizardStateData = {
  inputConnected: boolean;
  converterStarted: boolean;
  systemOn: boolean;
  resetInProgress: boolean;
  bmsStarting: boolean;
  converterStarting: boolean;
  transitioning: boolean;
  /** True once AlfenSystemMode reports Standby - ends the standby/shutdown transition wait early. */
  modeIsStandby: boolean;
  /** True once system_status reports Off - part of the shutdown transition's completion check. */
  systemOff: boolean;
};

export type BatteryInputConnection = {
  voltageL1L2In?: number | null;
  voltageL2L3In?: number | null;
  voltageL3L1In?: number | null;
  isBreakerClosed?: boolean | null;
};

export type ProgramWizardNumberFieldProps = {
  label: string;
  name: string;
  control: Control<FieldValues>;
  minValue: number;
  maxValue: number;
  defaultValue?: number | null;
  required?: boolean;
};

export type ProgramWizardProps = {
  batteryId: string;
  currentProgram?: ProgramWizardCurrentProgram | null;
  stateData: ProgramWizardStateData;
  label?: string;
  buttonSize?: ButtonProps['size'];
  paperStyle?: CSSProperties;
  isMobile?: boolean;
  NumberFieldComponent: ComponentType<ProgramWizardNumberFieldProps>;
  getBatteryConfig: () => Promise<ProgramWizardBatteryConfig>;
  onSubmit: (data: ProgramWizardData) => Promise<string | void>;
  onSubmitSuccess?: (message: string) => void;
  onSubmitError?: (error: unknown) => void;
  cancelButtonAlign?: 'left' | 'right';
  batteryInput?: BatteryInputConnection;
  getBatteryInput?: () => BatteryInputConnection;
};
